/* eslint-disable  */
import React, { createContext, useContext, useEffect, useState } from 'react';
import publicServices from '../service/publicServices';
import { LoadingContext } from './LoadingContext';
import CommonType from './types/CommonType';
import UserType from './types/UserType';
import { MsalContext } from '@azure/msal-react';
import { PATHS } from '../pages/App/constants';
import { msalInstance } from '..';
import _ from 'lodash';
import { matchPath } from 'react-router-dom';
import { setGrafanaUserSessionAttributes } from '../utils/grafana';
import { getAccessToken } from '../service';
import { loginRequest } from '../authConfig';
import { PopupRequest } from '@azure/msal-browser';
import logger from '../utils/logger';

export const EmptyUser: UserType = {
  adminMenuLinks: [],
  banners: [],
  apps: [],
  mediaApps: [],
  brandingObject: [],
  resources: [],
  insights: [],
  news: [],
  dhl: '',
  email: '',
  firstName: '',
  lastName: '',
  languageCode: '',
  roles: [],
  userName: '',
  lastLogin: '',
  countryIso: '',
  isRedirected: false,
  profileUpdated: false,
  isDhl: false,
};

export const UserContext = createContext({
  user: EmptyUser,
  isLogin: false,
  setUser: (user: UserType): void => {},
  refreshUser: (isDefault: boolean): void => {},
  loadUserNews: (): void => {},
  isNewsLoading: false,
  isAvatarLoading: false,
  insightsList: [],
  mediaTypes: []
});

export const UserProvider: React.FC<CommonType> = ({ children, ignorePath }) => {
  const [user, setUser] = useState<UserType>(EmptyUser);
  const [isLogin, setIsLogin] = useState(false);
  const [insightsList, setInsightsList] = useState([]);
  const [mediaTypes, setMediaTypes] = useState<[]>([]);
  const [isNewsLoading, setIsNewsLoading] = useState(false);
  const [isAvatarLoading, setIsAvatarLoading] = useState(false);
  const { setIsUserLoading } = useContext(LoadingContext);

  const { inProgress } = useContext(MsalContext);
  const isAutomaticLoginPage = matchPath(window.location.pathname, PATHS.HOME_AUTOMATIC_LOGIN);

  const filterChildAppsByCurrentDomain = (apps: any[]): any[] => {
    const allowedLifetrackHost = window.location.hostname.toLowerCase().includes('lifetrack');
    const allowedAppScope = allowedLifetrackHost ? 'Lifetrack' : 'DSC';

    if (!Array.isArray(apps)) {
      return apps;
    }

    return apps.map((appItem) => {
      if (appItem?.appScope != null && appItem.appScope !== allowedAppScope) {
        return null;
      }

      if (!Array.isArray(appItem?.childApps) || appItem.childApps.length === 0) {
        return appItem;
      }

      return {
        ...appItem,
        childApps: appItem.childApps.filter((childAppItem: any) => {
          try {
            // Children without appScope are not scoped to any domain and must stay visible.
            return childAppItem?.appScope == null || childAppItem.appScope === allowedAppScope;
          } catch {
            return false;
          }
        })
      };
    }).filter(Boolean);
  };

  const homePageRedirection = (): void => {
    if (window.location.pathname !== '/') {
      const win: Window = window;
      win.location = new URL(PATHS.HOME, window.location.origin).href;
    }
  };

  const loginOnAutoLoginHomepage = (): void => {
    const redirectToHomeRequest: PopupRequest = {
      ...loginRequest,
      redirectUri: new URL(PATHS.HOME, window.location.origin).href
    };
    getAccessToken(false, redirectToHomeRequest, redirectToHomeRequest)
      .then(() => {
        refreshUser(true);
      })
      .catch(err => logger.context.error('Automatic login failed:', err));
  };

  const refreshUser = (isDefault: boolean) => {
    logger.context.debug('refreshUser called, isDefault:', isDefault);
    setIsUserLoading(isDefault);
    setIsAvatarLoading(true);

    const msalActiveAccount = msalInstance.getActiveAccount();
    logger.context.debug('MSAL active account:', msalActiveAccount?.username || 'None');
    
    if (msalActiveAccount) {
      logger.context.debug('Loading user data for authenticated user');
      // Dynamically import protectedServices only for authenticated users
      import('../service/protectedServices').then(({ default: protectedServices }) => {
        logger.context.debug('Protected services loaded, making API calls');
        Promise.allSettled([
          protectedServices.getUser(),
          publicServices.getProfilePicture(),
          publicServices.getMSGraphUsersCountry(),
          protectedServices.getInsights('', 0, 10),
          protectedServices.getMediaTypes(),
          protectedServices.getUserBrandingObject()
        ])
        .then(([user, profilePicture, usersCountry, insights, mediaTypes, userBrandingObject]) => {
          logger.context.debug('API calls completed. User status:', user.status);
          logger.context.debug('ProfilePicture status:', profilePicture.status);
          logger.context.debug('UsersCountry status:', usersCountry.status);
          logger.context.debug('Insights status:', insights.status);
          logger.context.debug('MediaTypes status:', mediaTypes.status);
          logger.context.debug('UserBrandingObject status:', userBrandingObject.status);
          
          // Only when getUser fails we want to cancel everything, otherwise try to do with defaults.
          if (user.status === 'rejected') {
            logger.context.error('getUser failed:', user.reason);
            throw user.reason;
          }
          
          const userData = user.value.data;
          const userDataWithDomainFilteredChildApps = {
            ...userData,
            apps: filterChildAppsByCurrentDomain(userData?.apps || [])
          };
          logger.context.debug('User data received:', {
            email: userDataWithDomainFilteredChildApps?.email,
            firstName: userDataWithDomainFilteredChildApps?.firstName,
            lastName: userDataWithDomainFilteredChildApps?.lastName,
            isDhl: userDataWithDomainFilteredChildApps?.isDhl,
            impersonated: userDataWithDomainFilteredChildApps?.impersonated
          });
          
          if (user?.value.data?.impersonated == null || user?.value?.data?.impersonated === false) {
            setUser({
              ...userDataWithDomainFilteredChildApps,
              brandingObject: userBrandingObject.status === "fulfilled" ? userBrandingObject?.value?.data : null,
              avatar: profilePicture.status === 'fulfilled' ? profilePicture.value.data : null,
              news: [] // Initialize empty, will be loaded separately
            });
            // Avatar loading is complete (either loaded or failed)
            setIsAvatarLoading(false);
            logger.context.debug('Avatar loading complete. Status:', profilePicture.status);
          } else {
            logger.context.debug('User is impersonated');
            setUser({
              ...userDataWithDomainFilteredChildApps,
              news: [] // Initialize empty, will be loaded separately
            });
            // No avatar for impersonated users
            setIsAvatarLoading(false);
          }
          setInsightsList(insights.status === 'fulfilled' ? insights.value.data?.list || [] : []);
          if (mediaTypes.status === 'fulfilled' && !_.isEmpty(mediaTypes.value.data)) {
            setMediaTypes(mediaTypes.value.data);
          }
          setIsLogin(true);
          logger.context.debug('User login state set to true');
          
          if (usersCountry.status === 'fulfilled') {
            setGrafanaUserSessionAttributes(msalActiveAccount.localAccountId, msalActiveAccount.username, usersCountry.value);
          }

          if (isAutomaticLoginPage) {
            logger.context.debug('Redirecting from automatic login page to home');
            window.history.replaceState(null, '', PATHS.HOME);
          }
        })
        .catch((error) => {
          logger.context.error('Error loading user data:', error);
          setUser({} as UserType);
          setIsLogin(false);
          setIsAvatarLoading(false);
          homePageRedirection();
        })
        .finally(() => {
          setIsUserLoading(false);
          // Load news separately after user data is loaded
          if (msalActiveAccount) {
            loadUserNews();
          }
        });
      }).catch((importError) => {
        // Handle dynamic import error
        logger.context.error('Failed to load protected services:', importError);
        setUser({} as UserType);
        setIsLogin(false);
        setIsUserLoading(false);
        setIsAvatarLoading(false);
        homePageRedirection();
      });
    } else {
      logger.context.debug('No active MSAL account found');
      setIsUserLoading(false);
      setIsAvatarLoading(false);
      if (isAutomaticLoginPage) {
        logger.context.debug('Attempting automatic login');
        loginOnAutoLoginHomepage();
      } else if (window.location.pathname !== '/') {
        logger.context.debug('Redirecting to home page');
        homePageRedirection();
      }
    }
  };

  const loadUserNews = (): void => {
    const msalActiveAccount = msalInstance.getActiveAccount();
    if (!msalActiveAccount) {
      logger.context.debug('No active account, skipping news load');
      return;
    }

    setIsNewsLoading(true);
    logger.context.debug('Loading user news separately');
    
    // Dynamically import protectedServices for news loading
    import('../service/protectedServices').then(({ default: protectedServices }) => {
      protectedServices.getUserNews()
        .then((response) => {
          logger.context.debug('User news loaded successfully');
          setUser(prevUser => ({
            ...prevUser,
            news: response.data || []
          }));
        })
        .catch((error) => {
          logger.context.error('Error loading user news:', error);
          // Don't fail the entire flow for news, just keep empty array
          setUser(prevUser => ({
            ...prevUser,
            news: []
          }));
        })
        .finally(() => {
          setIsNewsLoading(false);
        });
    }).catch((importError) => {
      logger.context.error('Failed to load protected services for news:', importError);
      setIsNewsLoading(false);
    });
  };

  useEffect(() => {
    logger.context.debug('useEffect triggered. Current path:', window.location.pathname);
    logger.context.debug('MSAL inProgress status:', inProgress);
    
    const msalActiveAccount = msalInstance.getActiveAccount();
    const hasActiveAccount = msalActiveAccount !== null;
    logger.context.debug('Has active account:', hasActiveAccount);
    
    // Define paths that should NEVER trigger authentication when user is NOT authenticated
    const publicOnlyPaths = ['/privacyNotice'];
    
    // Check if current path should only be treated as public when not authenticated
    const isPublicOnlyPath = publicOnlyPaths.some(path => 
      window.location.pathname === path || window.location.pathname === path + '/'
    );
    
    logger.context.debug('Is public-only path:', isPublicOnlyPath);
    
    // If we're on a public-only path, do not attempt to load user data
    if (isPublicOnlyPath) {
      logger.context.debug('On public-only path, skipping user data load');
      return;
    }
    
    // For home path '/', only skip user data loading if there's no authenticated account
    if (window.location.pathname === '/' && !hasActiveAccount) {
      logger.context.debug('On home path without authentication, skipping user data load');
      return;
    }
    
    // prevent infinity loop. for expired or logout page we don't want to load current user
    var pathFound = false;
    const allKeys = Object.keys(PATHS) as Array<keyof typeof PATHS>;

    allKeys.forEach((keyData: keyof typeof PATHS) => {
      if (PATHS[keyData] && matchPath(PATHS[keyData], window.location.pathname)) {
        pathFound = true;
      }
    });

    logger.context.debug('Path found in PATHS:', pathFound);

    if (ignorePath != null && window.location.pathname != null) {
      for (let i = 0; i < ignorePath.length; i++) {
        if (window.location.pathname.includes(ignorePath[i])) {
          logger.context.debug('Path in ignore list, skipping user data load');
          return;
        }
      }
    }
    
    // Load user data when MSAL is ready and we have a valid path
    if (inProgress === 'none' && pathFound) {
      logger.context.debug('Loading user data - MSAL ready and valid path, hasActiveAccount:', hasActiveAccount);
      refreshUser(true);
    } else {
      logger.context.debug('Not loading user data - inProgress:', inProgress, 'pathFound:', pathFound, 'hasActiveAccount:', hasActiveAccount);
    }
  }, [inProgress === 'none']);

  // debugger;
  return <UserContext.Provider value={{ user, isLogin, setUser, refreshUser, loadUserNews, isNewsLoading, isAvatarLoading, insightsList, mediaTypes }}>{children}</UserContext.Provider>;
};
